import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { ClearanceApprovalTypeEnum } from "@/lib/enum/clearance-approval-type.enum";
import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ClearanceApprovalGoToHelper } from "@/lib/utility/clearance-approval.go-to-helper";
import { formatCurrency } from "@/lib/utility/currency-helper";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { formatYearsMonthDay } from "@/lib/utility/date-helper";
import { $t } from "@/lib/utility/t";
import clearanceApprovalRequestService from "@/services/mrfiktiv/services/clearance-approval-request.service";
import clearanceApprovalService from "@/services/mrfiktiv/services/clearance-approval.service";
import { SharedContentModule } from "@/store/modules/shared-content.store";
import {
  MrfiktivClearanceApprovalViewModelGen,
  MrfiktivCreateActivityLogDtoGen,
  MrfiktivCreateClearanceApprovalDtoGen,
  MrfiktivCreateRequestStatusHistoryElementDtoGen,
  MrfiktivStatusHistoryElementViewModelGen,
  MrfiktivUpdateClearanceApprovalDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { ClearanceApprovalDataAccessLayer } from "@/store/modules/access-layers/clearance-approval.access-layer";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { SharedContentDataAccessLayer } from "@/store/modules/access-layers/shared-content.access-layer";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import VueRouter from "vue-router";
import { ActivityLog, IActivityLog } from "./activity-log.entity";
import { Attachments, IAttachments } from "./attachments.entity";
import { IClearance } from "./clearance.entity";
import { IInvoiceLine, InvoiceLine } from "./invoice-line.entity";
import { IInvoiceParty, InvoiceParty } from "./invoice-party.entity";
import { IReference, Reference } from "./reference.entity";
import { IReport, Report } from "./report.entity";
import { ISharedContent } from "./shared-content.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { TagDataAccessLayer } from "@/store/modules/access-layers/tag.access-layer";
import { ITag, Tag } from "./tag.entity";
import { ISignDocument, SignDocument } from "./sign-document.entity";
import { SignDocumentDataAccessLayer } from "@/store/modules/access-layers/sign-document.access-layer";

@IsFilterable
class ClearanceApprovalBase implements IEntity<IClearanceApproval, MrfiktivUpdateClearanceApprovalDtoGen> {
  /** Id of the clearanceApproval object */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearanceApproval.id"
  })
  id: string;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.clearanceApproval.userId",
    config: {
      itemCallback: () => PartnerUserAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-user",
      refType: BackendResourceEnum.USER
    }
  })
  userId: string;

  /**
   * The title of the object
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearanceApproval.title"
  })
  title?: string;

  get titleReadable() {
    if (!this.title) {
      return $t("contract.noTitle");
    }

    return this.title;
  }

  /**
   * The description of the object
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearanceApproval.description"
  })
  description?: string;

  /**
   * Type of contract/service for the clearance approval
   */
  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.clearanceApproval.type",
    config: {
      items: Object.values(ClearanceApprovalTypeEnum).map((type) => ({
        value: type,
        text: `enums.ClearanceApprovalTypeEnum.${type}`
      })),
      itemValue: "value"
    }
  })
  type: ClearanceApprovalTypeEnum;

  /**
   * ID of the related estimate
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearanceApproval.estimateId"
  })
  estimateId?: string;

  /**
   * ID of the related document
   */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.clearanceApproval.documentId"
  })
  documentId?: string;

  get document(): ISignDocument | undefined {
    if (!this.documentId) return undefined;
    return SignDocumentDataAccessLayer.get(this.documentId);
  }
  /**
   * ID of the related report
   */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.documentId",
    config: {
      itemCallback: () => ReportDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-report",
      refType: BackendResourceEnum.REPORT
    }
  })
  reportId?: string;
  report: IReport | null = null;

  /**
   * total
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.clearanceApproval.total"
  })
  total?: number;

  /**
   * subTotal
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.clearanceApproval.subTotal"
  })
  subTotal?: number;

  get subTotalReadable() {
    if (this.subTotal === undefined || this.subTotal === null) {
      return "";
    }
    return formatCurrency(this.subTotal, this.currency);
  }

  /**
   * taxTotal
   */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.clearanceApproval.taxTotal"
  })
  taxTotal?: number;

  get totalReadable() {
    if (this.total === undefined || this.total === null) {
      return "";
    }
    return formatCurrency(this.total, this.currency);
  }

  /**
   * Due date of the entity.
   */
  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.clearanceApproval.due"
  })
  due?: string;

  get dueFormable(): string {
    if (!this.due) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.due));
  }

  set dueFormable(date: string) {
    if (date) {
      this.due = new Date(date).toISOString();
    } else {
      this.due = undefined;
    }
  }

  /** LineItems - if applicable */
  lineItems: IInvoiceLine[];

  /** Buyer - if applicable */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.buyer"
  })
  buyer?: IInvoiceParty;

  /** Seller - if applicable */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.invoice.seller"
  })
  seller?: IInvoiceParty;

  @FilterConfig({
    type: Reference
  })
  refs: IReference[];

  get refsCategories(): BackendResourceEnum[] {
    return [ResourceEnum.CLEARANCE];
  }

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.clearanceApproval.currency",
    config: {
      items: Object.values(CurrencyEnum)
    }
  })
  currency: CurrencyEnum;

  loading = false;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.clearanceApproval.status",
    config: {
      items: Object.values(RequestStatusEnum).map((status) => ({
        value: status,
        text: `enums.ClearanceApprovalRequestStatusEnum.${status}`
      })),
      itemValue: "value"
    }
  })
  status: RequestStatusEnum;

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  history: MrfiktivStatusHistoryElementViewModelGen[];

  @FilterConfig({
    displayName: "objects.clearanceApproval.assignees",
    type: FilterTypes.OBJECT_ID,
    width: "120",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-user",
      refType: BackendResourceEnum.USER
    }
  })
  assignees?: string[];

  attachments?: IAttachments;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.clearanceApproval.tagIds",
    config: {
      itemCallback: () => TagDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ value: item, size: "small" }),
      itemValue: "id",
      component: "tag-chip",
      refType: BackendResourceEnum.TAG
    }
  })
  tagIds: string[] = [];

  tags: ITag[] = [];

  isSharedContentLoading = false;

  get sharedContent(): ISharedContent[] {
    return SharedContentDataAccessLayer.entities.filter((sc) => sc.refs.some((r) => r.refId === this.id));
  }

  get lastSharedContent(): ISharedContent | undefined {
    return this.sharedContent
      .slice()
      .sort((a, b) => ((b.timestamp?.created ?? "") > (a.timestamp?.created ?? "") ? 1 : -1))[0];
  }

  get dueDate() {
    if (this.due) {
      return new Date(this.due);
    }
  }

  /**
   * Indicates if the given ticket has a due date
   */
  get isDue() {
    return this.due && this.canUpdate;
  }

  get totalGross() {
    if (!this.lineItems || this.lineItems.length === 0) return 0;
    return this.lineItems.reduce((acc, item) => acc + (1 + item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get totalNet() {
    if (!this.lineItems || this.lineItems.length === 0) return 0;
    return this.lineItems.reduce((acc, item) => acc + item.amount * item.quantity, 0);
  }

  get totalTax() {
    if (!this.lineItems || this.lineItems.length === 0) return 0;
    return this.lineItems.reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  get taxRates() {
    if (!this.lineItems || this.lineItems.length === 0) return [];
    return Array.from(new Set(this.lineItems.map((line) => line.taxRate))).filter((rate) => rate !== 0);
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  static get statusColorMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "grey"],
      [RequestStatusEnum.REQUESTED, "primary"],
      [RequestStatusEnum.REPLIED, "green"],
      [RequestStatusEnum.ACCEPTED, "green"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "red"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "red"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "orange"],
      [RequestStatusEnum.FINISHED, "deep-purple lighten-1"]
    ]);
  }

  static get statusIconMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUESTED, "mdi-send-variant-outline"],
      [RequestStatusEnum.REPLIED, "mdi-check"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "mdi-message-question-outline"],
      [RequestStatusEnum.ACCEPTED, "mdi-check"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "mdi-close-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "mdi-close-circle-outline"],
      [RequestStatusEnum.FINISHED, "mdi-check-bold"]
    ]);
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  get statusColor() {
    return ClearanceApprovalBase.statusColorMap.get(this.status);
  }

  /**
   * Icon - based on @see SIGN_COLOR_BY_STATUS
   */
  get statusIcon() {
    return ClearanceApprovalBase.statusIconMap.get(this.status);
  }

  get statusReadable() {
    return $t(`enums.ClearanceApprovalRequestStatusEnum.${this.status}`);
  }

  /**
   * Is the clearance approval completed
   */
  get isCompleted() {
    return [
      RequestStatusEnum.REPLIED,
      RequestStatusEnum.REJECTED_BY_REQUESTED,
      RequestStatusEnum.REJECTED_BY_REQUESTING,
      RequestStatusEnum.ACCEPTED,
      RequestStatusEnum.FINISHED
    ].includes(this.status);
  }

  /**
   * Is the clearance approval completed succesfully
   */
  get isCompletedSuccessfully() {
    return [RequestStatusEnum.REPLIED, RequestStatusEnum.ACCEPTED, RequestStatusEnum.FINISHED].includes(this.status);
  }

  get canUpdate() {
    return [RequestStatusEnum.DRAFT, RequestStatusEnum.REQUESTED].includes(this.status);
  }

  constructor(data: Partial<MrfiktivClearanceApprovalViewModelGen | IClearanceApproval>) {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";

    this.title = data.title;
    this.description = data.description;
    this.type = (data.type ?? ClearanceApprovalTypeEnum.WORKSHOP) as ClearanceApprovalTypeEnum;
    this.estimateId = data.estimateId;
    this.documentId = data.documentId;
    this.reportId = data.reportId;
    this.due = data.due;

    this.total = data.total;
    this.subTotal = data.subTotal;
    this.taxTotal = data.taxTotal;
    this.timestamp = new Timestamp(data.timestamp);
    this.lineItems = (data.lineItems ?? []).map((line) => new InvoiceLine(line as IInvoiceLine));
    this.buyer = data.buyer ? new InvoiceParty(data.buyer as IInvoiceParty) : undefined;
    this.seller = data.seller ? new InvoiceParty(data.seller as IInvoiceParty) : undefined;
    this.refs = Reference.filterDuplicates((data.refs ?? []).map((ref) => new Reference(ref as IReference)));
    this.status = (data.status ?? RequestStatusEnum.DRAFT) as RequestStatusEnum;
    this.currency = (data.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.history = data.history ?? [];
    this.assignees = data.assignees ?? [];
    this.tagIds = data.tagIds ?? [];
    this.tags = (data.tags ?? []).map((tag) => new Tag(tag));
    this.attachments = (data as IClearanceApproval).attachments
      ? new Attachments((data as IClearanceApproval).attachments as IAttachments)
      : undefined;
    this.mapPopulated(data);
  }

  map(data: MrfiktivClearanceApprovalViewModelGen): void {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";

    this.title = data.title;
    this.description = data.description;
    this.type = (data.type ?? ClearanceApprovalTypeEnum.WORKSHOP) as ClearanceApprovalTypeEnum;
    this.estimateId = data.estimateId;
    this.documentId = data.documentId;
    this.reportId = data.reportId;
    this.due = data.due;

    this.total = data.total;
    this.subTotal = data.subTotal;
    this.taxTotal = data.taxTotal;
    this.timestamp = new Timestamp(data.timestamp);
    this.lineItems = (data.lineItems ?? []).map((line) => new InvoiceLine(line as IInvoiceLine));
    this.buyer = data.buyer ? new InvoiceParty(data.buyer as IInvoiceParty) : undefined;
    this.seller = data.seller ? new InvoiceParty(data.seller as IInvoiceParty) : undefined;
    this.refs = Reference.filterDuplicates((data.refs ?? []).map((ref) => new Reference(ref as IReference)));
    this.status = (data.status ?? RequestStatusEnum.DRAFT) as RequestStatusEnum;
    this.currency = (data.currency ?? CurrencyEnum.EUR) as CurrencyEnum;
    this.history = data.history ?? [];
    this.assignees = data.assignees ?? [];
    this.tagIds = data.tagIds ?? [];
    this.tags = (data.tags ?? []).map((tag) => new Tag(tag));
    this.attachments = data.attachments ? new Attachments(data.attachments as IAttachments) : undefined;
    this.mapPopulated(data);
  }

  mapPopulated(populatedData: MrfiktivClearanceApprovalViewModelGen): void {
    const document = populatedData.document;
    if (document) {
      SignDocumentDataAccessLayer.set(new SignDocument(document));
    }
  }

  /**
   * Calculates all the line items for a given taxRate
   * @param taxRate the taxRate to calculate tax for
   * @returns
   */
  calcItemsPerTaxRate(taxRate: number): number {
    if (!this.lineItems || this.lineItems.length === 0) return 0;
    return this.lineItems
      .filter((l) => l.taxRate === taxRate)
      .reduce((acc, item) => acc + (item.taxRate / 100) * item.amount * item.quantity, 0);
  }

  async create(): Promise<this> {
    this.loading = true;
    try {
      const createDto: Partial<MrfiktivCreateClearanceApprovalDtoGen> = {
        title: this.title,
        description: this.description,
        type: this.type,
        estimateId: this.estimateId,
        documentId: this.documentId,
        reportId: this.reportId,
        due: this.due,
        lineItems: this.lineItems.map((item) => item.dto),
        seller: this.seller?.dto,
        buyer: this.buyer?.dto,
        refs: Reference.filterDuplicates(this.refs),
        currency: this.currency,
        total: this.total,
        subTotal: this.subTotal,
        taxTotal: this.taxTotal,
        assignees: this.assignees
      };
      const res = await clearanceApprovalService.create(
        this.partnerId,
        createDto as MrfiktivCreateClearanceApprovalDtoGen
      );

      this.map(res as MrfiktivClearanceApprovalViewModelGen);
      ClearanceApprovalDataAccessLayer.set(this);

      await this.fetchReportAndVehicle();
    } finally {
      this.loading = false;
    }

    await Promise.all([this.addCreateActivitiesOnReferences()]);

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await clearanceApprovalService.getOne(this.partnerId, this.id);
      this.map(res as MrfiktivClearanceApprovalViewModelGen);
      ClearanceApprovalDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      ClearanceApprovalDataAccessLayer.delete(this);
      throw e;
    }
    this.loading = false;

    return this;
  }

  async updatePartial(dto: MrfiktivUpdateClearanceApprovalDtoGen): Promise<this> {
    const res = await clearanceApprovalService.update(this.partnerId, this.id, dto);

    this.map(res as MrfiktivClearanceApprovalViewModelGen);
    ClearanceApprovalDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    this.loading = true;
    try {
      const updateDto: MrfiktivUpdateClearanceApprovalDtoGen = {
        title: this.title,
        description: this.description,
        type: this.type,
        estimateId: this.estimateId,
        documentId: this.documentId,
        reportId: this.reportId,
        due: this.due,
        lineItems: this.lineItems.map((item) => item.dto),
        seller: this.seller?.dto,
        buyer: this.buyer?.dto,
        refs: Reference.filterDuplicates(this.refs),
        currency: this.currency,
        total: this.total,
        subTotal: this.subTotal,
        taxTotal: this.taxTotal,
        assignees: this.assignees,
        tagIds: this.tags.map((tag) => tag.id)
      };
      await this.updatePartial(updateDto);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  async delete(): Promise<void> {
    this.loading = true;
    try {
      const res = await clearanceApprovalService.delete(this.partnerId, this.id);

      this.map(res as MrfiktivClearanceApprovalViewModelGen);
      ClearanceApprovalDataAccessLayer.delete(this);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
  }

  goTo(router: VueRouter) {
    return {
      table: () => new ClearanceApprovalGoToHelper(router).goToClearanceApprovalTable({ partnerId: this.partnerId }),
      detail: () =>
        new ClearanceApprovalGoToHelper(router).goToClearanceApprovalDetail({
          partnerId: this.partnerId,
          clearanceApprovalId: this.id
        })
    };
  }

  async accept(): Promise<this> {
    this.loading = true;
    try {
      const updated = await clearanceApprovalService.updateStatus(this.partnerId, this.id, RequestStatusEnum.ACCEPTED);

      this.map(updated as MrfiktivClearanceApprovalViewModelGen);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  async updateStatusLog() {
    await new ActivityLog().updateStatusLog({
      state: this.status,
      source: { refId: this.id, refType: ResourceEnum.CLEARANCE_APPROVAL },
      partnerId: this.partnerId
    });

    return this;
  }

  /**
   * Set the clearance approval status to FINISHED
   */
  async finish(): Promise<this> {
    this.loading = true;
    try {
      const updated = await clearanceApprovalService.updateStatus(this.partnerId, this.id, RequestStatusEnum.FINISHED);
      this.map(updated as MrfiktivClearanceApprovalViewModelGen);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;
    return this;
  }

  async reject(): Promise<this> {
    this.loading = true;
    try {
      const updated = await clearanceApprovalService.updateStatus(
        this.partnerId,
        this.id,
        RequestStatusEnum.REJECTED_BY_REQUESTING
      );

      this.map(updated as MrfiktivClearanceApprovalViewModelGen);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  /**
   * Respond to an approval request (e.g. accept)
   */
  async respondAccept(token: string, file?: File, documentAttachments?: File[]): Promise<this> {
    this.loading = true;
    try {
      const response = await clearanceApprovalRequestService.respondAccept(this.partnerId, this.id, token, {
        file,
        ...(documentAttachments?.length ? { documentAttachments } : {})
      });

      this.map(response as MrfiktivClearanceApprovalViewModelGen);

      ClearanceApprovalDataAccessLayer.set(this);
    } finally {
      this.loading = false;
    }

    return this;
  }

  /**
   * Respond to an approval request (e.g. reject)
   */
  async respondReject(token: string, data: MrfiktivCreateRequestStatusHistoryElementDtoGen): Promise<this> {
    this.loading = true;
    try {
      const response = await clearanceApprovalRequestService.respondReject(this.partnerId, this.id, token, data);

      this.map(response as MrfiktivClearanceApprovalViewModelGen);

      ClearanceApprovalDataAccessLayer.set(this);
    } finally {
      this.loading = false;
    }

    return this;
  }

  /**
   * Adds activity logs celebrating the creation of the clearance approval
   * @returns the created activity logs
   */
  addCreateActivitiesOnReferences(): Promise<IActivityLog[]> {
    const sources = [...this.refs];
    if (
      this.estimateId &&
      !sources.some((ref) => ref.refType === BackendResourceEnum.REPAIR_ESTIMATE && ref.refId === this.estimateId)
    ) {
      sources.push(
        new Reference({
          refType: BackendResourceEnum.REPAIR_ESTIMATE,
          refId: this.estimateId
        })
      );
    }
    if (
      this.reportId &&
      !sources.some((ref) => ref.refType === BackendResourceEnum.REPORT && ref.refId === this.reportId)
    ) {
      sources.push(
        new Reference({
          refType: BackendResourceEnum.REPORT,
          refId: this.reportId
        })
      );
    }
    const target = {
      refType: BackendResourceEnum.CLEARANCE_APPROVAL,
      refId: this.id
    };

    const activityAsync = [];

    for (const source of sources) {
      const data: MrfiktivCreateActivityLogDtoGen = {
        source: source,
        target: [target],
        actionType: ActionEnum.CREATE,
        activity: ActivityTypeEnum.CREATE_REF
      };

      activityAsync.push(
        new ActivityLog({
          partnerId: this.partnerId,
          ...data
        }).create()
      );
    }

    return Promise.all(activityAsync);
  }

  /**
   * Create an activity log for assignee changes
   * @param newAssignees The ids of the new assignees of the ticket
   * @param activityType Whether the assignee was added or removed
   */
  async createAssigneeActivity(
    activityType: ActivityTypeEnum.CREATE_ASSIGNEE | ActivityTypeEnum.DELETE_ASSIGNEE,
    newAssignees?: string[]
  ) {
    if (!newAssignees?.length) return;

    await new ActivityLog().createAssigneeActivity({
      partnerId: this.partnerId,
      source: {
        refType: ResourceEnum.CLEARANCE_APPROVAL,
        refId: this.id
      },
      newAssignees,
      activityType
    });
  }

  async fetchReportAndVehicle() {
    this.report = null;

    const reportId = this.reportId;
    if (!reportId) {
      return;
    }

    this.report =
      ReportDataAccessLayer.get(reportId) ||
      (await new Report({
        id: reportId,
        partnerId: this.partnerId
      }).fetch()) ||
      null;

    await this.report?.fetchVehicle();
  }

  mapFromClearance(clearance: IClearance) {
    this.partnerId = clearance?.partnerId;
    this.seller = new InvoiceParty();
    this.estimateId = clearance.estimateId;
    this.title = clearance.title;
    this.lineItems = clearance.lineItems;
    this.assignees = clearance.assignees;
    this.refs = clearance.refs;
    this.reportId = clearance.reportId;
    this.currency = clearance.currency;

    return this;
  }

  async fetchSharedContent() {
    this.isSharedContentLoading = true;
    try {
      await SharedContentModule.fetchByRef({ partnerId: this.partnerId, refId: this.id });
    } finally {
      this.isSharedContentLoading = false;
    }
  }
}

type IClearanceApproval = ClearanceApprovalBase;
const ClearanceApproval = Filter.createForClass(ClearanceApprovalBase);

export { ClearanceApproval, IClearanceApproval };
